import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePortfolioStore } from '../../../hooks/usePortfolioStore';

export default function PortfolioSection() {
  const [visible, setVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const ref = useRef<HTMLElement>(null);
  const { items, loading } = usePortfolioStore();

  // 홈에선 최대 6장만 미리보기
  const preview = items.slice(0, 6);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="portfolio" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Title Row */}
        <div
          className={`flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div>
            <span className="text-stone-400 text-sm font-bold tracking-widest uppercase">Portfolio</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mt-2">시공 사례</h2>
          </div>
          <p className="text-stone-500 text-base md:text-lg max-w-xs">
            라인 인테리어가 직접 완성한 시공 현장들입니다
          </p>
        </div>

        {/* Grid */}
        <div
          className={`transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-7 h-7 border-2 border-stone-200 border-t-stone-600 rounded-full animate-spin" />
            </div>
          ) : preview.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed border-stone-200">
              <i className="ri-image-2-line text-4xl text-stone-200 mb-4" />
              <p className="text-stone-500 font-medium mb-1">아직 등록된 시공 사례가 없습니다</p>
              <p className="text-stone-400 text-sm mb-5">사진 관리에서 시공 사례를 추가해보세요</p>
              <Link
                to="/portfolio/manage"
                className="px-6 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-stone-700 transition-colors whitespace-nowrap"
              >
                사진 추가하기
              </Link>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-2">
              {preview.map((item) => (
                <div
                  key={item.id}
                  className="break-inside-avoid mb-2 relative overflow-hidden cursor-pointer group rounded-lg"
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <img
                    src={item.imageUrl}
                    alt="시공 사례"
                    className="w-full h-auto object-cover block transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    className={`absolute inset-0 bg-black/30 transition-opacity duration-300 flex items-center justify-center ${
                      hoveredId === item.id ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                      <i className="ri-zoom-in-line text-white text-lg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* View All Button */}
        {preview.length > 0 && (
          <div
            className={`mt-10 flex justify-center transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-3 border border-stone-200 rounded-full text-stone-700 text-sm font-medium hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              전체 포트폴리오 보기
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
